import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { addDays, addMinutes, format, isSameDay, parse, startOfDay, startOfWeek } from 'date-fns';
import {
    LayoutDashboard, Scissors, Users, Calendar, LogOut, Menu, X,
    TrendingUp, Euro, Clock, ChevronDown, ChevronLeft, ChevronRight, Plus, Edit, Trash2, Check, Megaphone, Image
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, servicesAPI, barbersAPI, appointmentsAPI, promotionsAPI, vipPerksAPI, mediaAPI } from '../lib/api';
import { toast } from 'sonner';

// Sidebar Navigation
const Sidebar = ({ isOpen, setIsOpen }) => {
    const location = useLocation();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const navItems = [
        { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/admin/appointments', label: 'Appointments', icon: Calendar },
        { path: '/admin/services', label: 'Services', icon: Scissors },
        { path: '/admin/barbers', label: 'Barbers', icon: Users },
        { path: '/admin/offers', label: 'Offers', icon: Megaphone },
        { path: '/admin/media', label: 'Media', icon: Image },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 h-full w-64 bg-dark-paper border-r border-zinc-800 z-50 transform transition-transform lg:translate-x-0 ${
                    isOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="p-6">
                    {/* Logo */}
                    <div className="flex items-center gap-3 mb-8">
                        <img
                            src="https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/03303zlc_96f21524-c6ba-4411-86fb-3aa42208d697.jpeg"
                            alt="RL"
                            className="h-10 w-10"
                        />
                        <div>
                            <h1 className="font-anton text-lg">RL ADMIN</h1>
                            <p className="text-zinc-500 text-xs font-urbanist">{user?.name}</p>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="space-y-2">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setIsOpen(false)}
                                    data-testid={`nav-${item.label.toLowerCase()}`}
                                    className={`flex items-center gap-3 px-4 py-3 font-urbanist transition-colors ${
                                        isActive
                                            ? 'bg-primary/10 text-primary border-l-2 border-primary'
                                            : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                                    }`}
                                >
                                    <item.icon size={20} />
                                    {item.label}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Logout */}
                    <div className="absolute bottom-6 left-6 right-6">
                        <button
                            onClick={handleLogout}
                            data-testid="admin-logout-btn"
                            className="flex items-center gap-3 px-4 py-3 w-full text-zinc-400 hover:text-white font-urbanist"
                        >
                            <LogOut size={20} />
                            Logout
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
};

// Dashboard Overview
const DashboardOverview = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadStats = async () => {
            try {
                const response = await dashboardAPI.getStats();
                setStats(response.data);
            } catch (error) {
                console.error('Failed to load stats:', error);
            } finally {
                setLoading(false);
            }
        };
        loadStats();
    }, []);

    const statCards = stats ? [
        { label: "Today's Appointments", value: stats.today_appointments, icon: Calendar, color: 'text-primary' },
        { label: 'Monthly Appointments', value: stats.monthly_appointments, icon: TrendingUp, color: 'text-accent-cyan' },
        { label: 'Monthly Revenue', value: `€${stats.monthly_revenue}`, icon: Euro, color: 'text-accent-yellow' },
        { label: 'Active Barbers', value: stats.active_barbers, icon: Users, color: 'text-accent-pink' },
    ] : [];

    return (
        <div data-testid="admin-dashboard">
            <h1 className="font-anton text-3xl mb-8">DASHBOARD</h1>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="stat-card animate-pulse">
                            <div className="h-4 bg-zinc-800 rounded w-1/2 mb-4" />
                            <div className="h-10 bg-zinc-800 rounded w-1/3" />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {statCards.map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="stat-card"
                            data-testid={`stat-card-${i}`}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-zinc-500 font-urbanist text-sm">{stat.label}</span>
                                <stat.icon className={stat.color} size={20} />
                            </div>
                            <div className="stat-value">{stat.value}</div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Appointments Management
const AppointmentsManagement = () => {
    const [appointments, setAppointments] = useState([]);
    const [services, setServices] = useState([]);
    const [barbers, setBarbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('agenda');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedStaffKey, setSelectedStaffKey] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        const loadLookups = async () => {
            try {
                const [servicesRes, barbersRes] = await Promise.all([
                    servicesAPI.getAll(),
                    barbersAPI.getAll()
                ]);
                setServices(servicesRes.data);
                setBarbers(barbersRes.data);
            } catch (error) {
                console.error('Failed to load lookups:', error);
            }
        };
        loadLookups();
    }, []);

    const getRangeDates = () => {
        const start = startOfDay(selectedDate);
        if (viewMode === 'day') {
            return { start, end: start };
        }
        if (viewMode === '3day') {
            return { start, end: addDays(start, 2) };
        }
        return { start, end: addDays(start, 30) };
    };

    useEffect(() => {
        const loadAppointments = async () => {
            try {
                setLoading(true);
                const { start, end } = getRangeDates();
                const params = {
                    start_date: format(start, 'yyyy-MM-dd'),
                    end_date: format(end, 'yyyy-MM-dd')
                };
                if (selectedStaffKey !== 'all') {
                    params.staff_key = selectedStaffKey;
                }
                const response = await appointmentsAPI.getAll(params);
                setAppointments(response.data);
            } catch (error) {
                console.error('Failed to load appointments:', error);
            } finally {
                setLoading(false);
            }
        };

        loadAppointments();
    }, [viewMode, selectedDate, selectedStaffKey]);

    const staffBranchMap = useMemo(() => {
        return barbers.reduce((acc, barber) => {
            const name = (barber?.name || '').toLowerCase();
            if (name.includes('swieqi')) {
                acc[barber.key] = 'swieqi';
            }
            if (name.includes('san gwann') || name.includes('san ġwann') || name.includes('sangwann')) {
                acc[barber.key] = 'san_gwann';
            }
            return acc;
        }, {});
    }, [barbers]);

    const servicesByKey = useMemo(() => {
        return services.reduce((acc, service) => {
            acc[service.key] = service;
            return acc;
        }, {});
    }, [services]);

    const barbersByKey = useMemo(() => {
        return barbers.reduce((acc, barber) => {
            acc[barber.key] = barber;
            return acc;
        }, {});
    }, [barbers]);

    const enrichedAppointments = useMemo(() => {
        return appointments.map((apt) => {
            const service = servicesByKey[apt.service_key];
            const barber = barbersByKey[apt.barber_key];
            const serviceName = service?.name || apt.service_name || 'Service';
            const barberName = barber?.name || apt.barber_name || 'Any';
            const branch = staffBranchMap[apt.barber_key]
                || (serviceName.toLowerCase().includes('swieqi') ? 'swieqi' : serviceName.toLowerCase().includes('san gwann') || serviceName.toLowerCase().includes('san ġwann') ? 'san_gwann' : 'unknown');

            const startDateTime = parse(`${apt.date} ${apt.time}`, 'yyyy-MM-dd HH:mm', new Date());
            const endDateTime = addMinutes(startDateTime, apt.duration || 30);
            const timeRange = `${format(startDateTime, 'h:mm a')} - ${format(endDateTime, 'h:mm a')}`;

            return {
                ...apt,
                serviceName,
                barberName,
                branch,
                timeRange
            };
        });
    }, [appointments, servicesByKey, barbersByKey, staffBranchMap]);

    const visibleAppointments = useMemo(() => {
        if (statusFilter === 'all') return enrichedAppointments;
        return enrichedAppointments.filter((apt) => apt.status === statusFilter);
    }, [enrichedAppointments, statusFilter]);

    const groupedAppointments = useMemo(() => {
        return visibleAppointments.reduce((acc, apt) => {
            if (!acc[apt.date]) {
                acc[apt.date] = [];
            }
            acc[apt.date].push(apt);
            return acc;
        }, {});
    }, [visibleAppointments]);

    const sortedDates = useMemo(() => {
        return Object.keys(groupedAppointments).sort();
    }, [groupedAppointments]);

    const shiftDays = viewMode === 'agenda' ? 7 : viewMode === '3day' ? 3 : 1;
    const handleDateShift = (direction) => {
        setSelectedDate(addDays(selectedDate, direction * shiftDays));
    };

    const getDateRangeLabel = () => {
        const { start, end } = getRangeDates();
        if (format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')) {
            return format(start, 'EEEE, MMM d');
        }
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;
    };

    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
    const weekEnd = addDays(weekStart, 6);
    const weekHeaderLabel = format(weekStart, 'MMM') === format(weekEnd, 'MMM')
        ? `${format(weekStart, 'MMM')} ${format(weekEnd, 'yyyy')}`
        : `${format(weekStart, 'MMM')} - ${format(weekEnd, 'MMM')} ${format(weekEnd, 'yyyy')}`;

    const handleTodayShortcut = () => {
        setSelectedDate(new Date());
    };

    const handleThisWeekShortcut = () => {
        setSelectedDate(startOfWeek(new Date(), { weekStartsOn: 1 }));
    };

    const branchLabelMap = {
        swieqi: 'Swieqi Branch',
        san_gwann: 'San Gwann Branch',
        unknown: 'Branch'
    };

    const branchColorMap = {
        swieqi: 'bg-primary',
        san_gwann: 'bg-accent-cyan',
        unknown: 'bg-zinc-600'
    };

    const statusBadge = (status) => {
        const classes = {
            pending: 'status-badge status-pending',
            confirmed: 'status-badge status-confirmed',
            completed: 'status-badge status-completed',
            cancelled: 'status-badge status-cancelled',
        };
        return <span className={classes[status] || classes.confirmed}>{status}</span>;
    };

    return (
        <div data-testid="admin-appointments">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <h1 className="font-anton text-3xl">APPOINTMENTS</h1>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40 bg-dark-paper border-zinc-700" data-testid="status-filter">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="grid lg:grid-cols-[280px_1fr] gap-6">
                <aside className="bg-dark-paper border border-zinc-800 p-4" data-testid="calendar-sidebar">
                    <div className="mb-6">
                        <p className="text-zinc-500 text-xs uppercase font-urbanist mb-3">View Mode</p>
                        <div className="space-y-2">
                            {['agenda', 'day', '3day'].map((mode) => (
                                <button
                                    key={mode}
                                    onClick={() => setViewMode(mode)}
                                    data-testid={`view-mode-${mode}`}
                                    className={`w-full text-left px-3 py-2 border transition-colors font-urbanist ${
                                        viewMode === mode
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
                                    }`}
                                >
                                    {mode === 'agenda' ? 'Agenda' : mode === 'day' ? 'Day' : '3 Day'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mb-6">
                        <p className="text-zinc-500 text-xs uppercase font-urbanist mb-3">Start Date</p>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between px-3 py-2 border border-zinc-700 bg-transparent text-left"
                                    data-testid="calendar-date-trigger"
                                >
                                    <span className="font-urbanist text-white">
                                        {format(selectedDate, 'dd MMM yyyy')}
                                    </span>
                                    <Calendar size={16} className="text-zinc-500" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="bg-dark-paper border-zinc-800 p-3" align="start">
                                <CalendarComponent
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={(date) => date && setSelectedDate(date)}
                                    data-testid="calendar-date-picker"
                                />
                            </PopoverContent>
                        </Popover>
                        <div className="flex gap-2 mt-3">
                            <button
                                type="button"
                                onClick={handleTodayShortcut}
                                data-testid="calendar-shortcut-today"
                                className="flex-1 border border-zinc-800 text-zinc-400 text-xs font-urbanist py-2 hover:text-white hover:border-zinc-600"
                            >
                                Today
                            </button>
                            <button
                                type="button"
                                onClick={handleThisWeekShortcut}
                                data-testid="calendar-shortcut-week"
                                className="flex-1 border border-zinc-800 text-zinc-400 text-xs font-urbanist py-2 hover:text-white hover:border-zinc-600"
                            >
                                This Week
                            </button>
                        </div>
                    </div>

                    <div>
                        <p className="text-zinc-500 text-xs uppercase font-urbanist mb-3">Team</p>
                        <button
                            onClick={() => setSelectedStaffKey('all')}
                            data-testid="calendar-filter-all"
                            className={`w-full text-left px-3 py-2 border transition-colors font-urbanist mb-2 ${
                                selectedStaffKey === 'all'
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
                            }`}
                        >
                            All Schedules
                        </button>
                        <div className="space-y-2">
                            {barbers.map((barber) => (
                                <button
                                    key={barber.key}
                                    onClick={() => setSelectedStaffKey(barber.key)}
                                    data-testid={`calendar-filter-${barber.key}`}
                                    className={`w-full text-left px-3 py-2 border transition-colors font-urbanist ${
                                        selectedStaffKey === barber.key
                                            ? 'border-accent-cyan bg-accent-cyan/10 text-accent-cyan'
                                            : 'border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
                                    }`}
                                >
                                    {barber.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </aside>

                <div className="space-y-6" data-testid="calendar-agenda">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <p className="text-zinc-500 text-sm font-urbanist">Calendar View</p>
                            <h2 className="font-anton text-2xl">{getDateRangeLabel()}</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                onClick={() => handleDateShift(-1)}
                                data-testid="calendar-prev"
                                className="border-zinc-700 text-zinc-400 hover:text-white"
                            >
                                <ChevronLeft size={16} />
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => handleDateShift(1)}
                                data-testid="calendar-next"
                                className="border-zinc-700 text-zinc-400 hover:text-white"
                            >
                                <ChevronRight size={16} />
                            </Button>
                        </div>
                    </div>

                    <div className="bg-dark-paper border border-zinc-800 p-4" data-testid="calendar-week-strip">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-zinc-500 text-xs uppercase font-urbanist">Week</p>
                            <span className="font-anton text-lg">{weekHeaderLabel}</span>
                        </div>
                        <div className="grid grid-cols-7 gap-2">
                            {weekDays.map((day) => {
                                const dayKey = format(day, 'yyyy-MM-dd');
                                const isSelected = isSameDay(day, selectedDate);
                                return (
                                    <button
                                        key={dayKey}
                                        type="button"
                                        onClick={() => setSelectedDate(day)}
                                        data-testid={`calendar-weekday-${dayKey}`}
                                        className={`flex flex-col items-center justify-center border px-2 py-2 transition-colors ${
                                            isSelected
                                                ? 'border-primary bg-primary text-black'
                                                : 'border-zinc-800 text-white hover:border-zinc-600'
                                        }`}
                                    >
                                        <span className={`text-xs ${isSelected ? 'text-black' : 'text-zinc-500'}`}>
                                            {format(day, 'EEE')}
                                        </span>
                                        <span className="mt-1 text-sm font-anton">
                                            {format(day, 'd')}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {loading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-dark-paper border border-zinc-800 p-4 animate-pulse">
                                    <div className="h-4 bg-zinc-800 rounded w-1/3 mb-3" />
                                    <div className="h-6 bg-zinc-800 rounded w-2/3" />
                                </div>
                            ))}
                        </div>
                    ) : sortedDates.length === 0 ? (
                        <div className="text-center py-12 text-zinc-500 font-urbanist">
                            No appointments found for this range.
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {sortedDates.map((dateKey) => (
                                <div key={dateKey} className="bg-dark-paper border border-zinc-800" data-testid={`agenda-date-${dateKey}`}>
                                    <div className="px-4 py-3 border-b border-zinc-800">
                                        <p className="text-zinc-400 text-sm">{format(parse(dateKey, 'yyyy-MM-dd', new Date()), 'EEEE, MMM d')}</p>
                                    </div>
                                    <div className="divide-y divide-zinc-800">
                                        {groupedAppointments[dateKey]
                                            .sort((a, b) => a.time.localeCompare(b.time))
                                            .map((apt) => (
                                                <div key={apt.id} className="flex items-start gap-4 p-4" data-testid={`appointment-card-${apt.id}`}>
                                                    <div className={`w-1.5 rounded-full ${branchColorMap[apt.branch] || branchColorMap.unknown}`} />
                                                    <div className="flex-1">
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                                            <div>
                                                                <p className="font-urbanist font-semibold text-white">{apt.customer_name}</p>
                                                                <p className="text-zinc-400 text-sm">{apt.serviceName} • {branchLabelMap[apt.branch] || branchLabelMap.unknown}</p>
                                                            </div>
                                                            <div className="text-zinc-400 text-sm">{apt.timeRange}</div>
                                                        </div>
                                                        <div className="flex items-center justify-between mt-2">
                                                            <p className="text-zinc-500 text-sm">Barber: {apt.barberName}</p>
                                                            {statusBadge(apt.status)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Services Management
const ServicesManagement = () => {
    const [services, setServices] = useState([]);
    const [barbers, setBarbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const isReadOnly = true;
    const [formData, setFormData] = useState({
        name: '',
        price: '',
        duration: '30',
        branch: 'swieqi',
        category: 'haircut'
    });

    useEffect(() => {
        loadServices();
    }, []);

    const loadServices = async () => {
        try {
            const [servicesResponse, barbersResponse] = await Promise.all([
                servicesAPI.getAll(),
                barbersAPI.getAll()
            ]);
            setServices(servicesResponse.data);
            setBarbers(barbersResponse.data);
        } catch (error) {
            console.error('Failed to load services:', error);
        } finally {
            setLoading(false);
        }
    };

    const staffBranchMap = useMemo(() => {
        return barbers.reduce((acc, barber) => {
            const name = (barber?.name || '').toLowerCase();
            if (name.includes('swieqi')) {
                acc[barber.key] = 'swieqi';
            }
            if (name.includes('san gwann') || name.includes('san ġwann') || name.includes('sangwann')) {
                acc[barber.key] = 'san_gwann';
            }
            return acc;
        }, {});
    }, [barbers]);

    const getServiceBranches = (service) => {
        const branches = new Set();
        const name = (service?.name || '').toLowerCase();

        if (name.includes('swieqi')) {
            branches.add('swieqi');
        }
        if (name.includes('san gwann') || name.includes('san ġwann') || name.includes('sangwann')) {
            branches.add('san_gwann');
        }

        if (service?.staff_keys?.length) {
            service.staff_keys.forEach((key) => {
                const branch = staffBranchMap[key];
                if (branch) {
                    branches.add(branch);
                }
            });
        }

        if (branches.size === 0) {
            return ['swieqi', 'san_gwann'];
        }

        return Array.from(branches);
    };

    const getBranchLabel = (service) => {
        const branches = getServiceBranches(service);
        if (branches.length > 1) {
            return 'Swieqi + San Gwann';
        }
        if (branches.includes('swieqi')) {
            return 'Swieqi Branch';
        }
        if (branches.includes('san_gwann')) {
            return 'San Gwann Branch';
        }
        return 'Branch';
    };

    const handleSubmit = async () => {
        try {
            const data = {
                ...formData,
                price: parseFloat(formData.price),
                duration: parseInt(formData.duration)
            };

            if (editingService) {
                await servicesAPI.update(editingService.id, data);
                toast.success('Service updated');
            } else {
                await servicesAPI.create(data);
                toast.success('Service created');
            }
            
            setIsDialogOpen(false);
            setEditingService(null);
            setFormData({ name: '', price: '', duration: '30', branch: 'swieqi', category: 'haircut' });
            loadServices();
        } catch (error) {
            toast.error('Failed to save service');
        }
    };

    const handleEdit = (service) => {
        setEditingService(service);
        const branches = getServiceBranches(service);
        setFormData({
            name: service.name,
            price: service.price.toString(),
            duration: service.duration.toString(),
            branch: branches.includes('san_gwann') ? 'san_gwann' : 'swieqi',
            category: service.category || 'haircut'
        });
        setIsDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this service?')) return;
        try {
            await servicesAPI.delete(id);
            toast.success('Service deleted');
            loadServices();
        } catch (error) {
            toast.error('Failed to delete service');
        }
    };

    return (
        <div data-testid="admin-services">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-anton text-3xl">SERVICES</h1>
                    <p className="text-zinc-500 font-urbanist text-sm" data-testid="services-setmore-banner">
                        Managed in Setmore (read-only)
                    </p>
                </div>
                {!isReadOnly && (
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                onClick={() => {
                                    setEditingService(null);
                                    setFormData({ name: '', price: '', duration: '30', branch: 'swieqi', category: 'haircut' });
                                }}
                                data-testid="add-service-btn"
                                className="bg-primary text-black font-anton tracking-wider"
                            >
                                <Plus size={18} className="mr-2" />
                                ADD SERVICE
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-dark-paper border-zinc-800">
                            <DialogHeader>
                                <DialogTitle className="font-anton text-xl">
                                    {editingService ? 'EDIT SERVICE' : 'ADD SERVICE'}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                                <Input
                                    placeholder="Service Name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    data-testid="service-name-input"
                                    className="bg-transparent border-zinc-700"
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <Input
                                        type="number"
                                        placeholder="Price (€)"
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        data-testid="service-price-input"
                                        className="bg-transparent border-zinc-700"
                                    />
                                    <Input
                                        type="number"
                                        placeholder="Duration (min)"
                                        value={formData.duration}
                                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                                        data-testid="service-duration-input"
                                        className="bg-transparent border-zinc-700"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <Select value={formData.branch} onValueChange={(v) => setFormData({ ...formData, branch: v })}>
                                        <SelectTrigger className="bg-transparent border-zinc-700">
                                            <SelectValue placeholder="Branch" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="swieqi">Swieqi</SelectItem>
                                            <SelectItem value="san_gwann">San Gwann</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                                        <SelectTrigger className="bg-transparent border-zinc-700">
                                            <SelectValue placeholder="Category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="haircut">Haircut</SelectItem>
                                            <SelectItem value="beard">Beard</SelectItem>
                                            <SelectItem value="color">Color</SelectItem>
                                            <SelectItem value="extras">Extras</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button
                                    onClick={handleSubmit}
                                    data-testid="save-service-btn"
                                    className="w-full bg-primary text-black font-anton tracking-wider"
                                >
                                    {editingService ? 'UPDATE' : 'CREATE'} SERVICE
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                    [1, 2, 3].map((i) => (
                        <div key={i} className="bg-dark-paper border border-zinc-800 p-4 animate-pulse">
                            <div className="h-6 bg-zinc-800 rounded w-3/4 mb-2" />
                            <div className="h-4 bg-zinc-800 rounded w-1/2" />
                        </div>
                    ))
                ) : (
                    services.map((service) => (
                        <div
                            key={service.id}
                            className="bg-dark-paper border border-zinc-800 p-4 card-hover"
                            data-testid={`service-card-${service.id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="font-urbanist font-semibold text-white">{service.name}</h3>
                                    <p className="text-primary font-anton text-xl">€{service.price}</p>
                                    <p className="text-zinc-500 text-sm flex items-center gap-2 mt-1">
                                        <Clock size={14} /> {service.duration} min
                                        <span>• {getBranchLabel(service)}</span>
                                    </p>
                                </div>
                                {!isReadOnly && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEdit(service)}
                                            className="p-2 text-zinc-500 hover:text-white"
                                            data-testid={`edit-service-${service.id}`}
                                        >
                                            <Edit size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(service.id)}
                                            className="p-2 text-zinc-500 hover:text-accent-pink"
                                            data-testid={`delete-service-${service.id}`}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// Barbers Management
const BarbersManagement = () => {
    const [barbers, setBarbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingKey, setTogglingKey] = useState(null);

    const getBarberBranchLabel = (barber) => {
        const name = (barber?.name || '').toLowerCase();
        if (name.includes('swieqi')) return 'Swieqi Branch';
        if (name.includes('san gwann') || name.includes('san ġwann') || name.includes('sangwann')) return 'San Gwann Branch';
        return 'Branch';
    };

    useEffect(() => {
        loadBarbers();
    }, []);

    const loadBarbers = async () => {
        try {
            const response = await barbersAPI.getAll();
            setBarbers(response.data);
        } catch (error) {
            console.error('Failed to load barbers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAvailability = async (barber) => {
        setTogglingKey(barber.key);
        try {
            const newDisabled = !barber.disabled;
            await barbersAPI.toggleAvailability(barber.key, newDisabled);
            toast.success(newDisabled ? `${barber.name} disattivato dal booking` : `${barber.name} riattivato sul booking`);
            loadBarbers();
        } catch (error) {
            toast.error('Errore nel cambiare disponibilità');
        } finally {
            setTogglingKey(null);
        }
    };

    return (
        <div data-testid="admin-barbers">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-anton text-3xl">BARBERS</h1>
                    <p className="text-zinc-500 font-urbanist text-sm" data-testid="barbers-setmore-banner">
                        Synced from Setmore · Toggle availability below
                    </p>
                </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    [1, 2, 3].map((i) => (
                        <div key={i} className="bg-dark-paper border border-zinc-800 p-6 animate-pulse">
                            <div className="w-20 h-20 bg-zinc-800 rounded-full mb-4" />
                            <div className="h-6 bg-zinc-800 rounded w-3/4 mb-2" />
                            <div className="h-4 bg-zinc-800 rounded w-1/2" />
                        </div>
                    ))
                ) : (
                    barbers.map((barber) => (
                        <div
                            key={barber.id}
                            className={`bg-dark-paper border p-6 card-hover transition-all ${barber.disabled ? 'border-red-800/50 opacity-60' : 'border-zinc-800'}`}
                            data-testid={`barber-card-${barber.id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        <img
                                            src={barber.image_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200'}
                                            alt={barber.name}
                                            className={`w-16 h-16 rounded-full object-cover ${barber.disabled ? 'grayscale' : ''}`}
                                        />
                                        {barber.disabled && (
                                            <div className="absolute inset-0 bg-red-900/30 rounded-full flex items-center justify-center">
                                                <X size={24} className="text-red-400" />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="font-urbanist font-semibold text-white text-lg">{barber.name}</h3>
                                        <p className="text-primary text-sm">{getBarberBranchLabel(barber)}</p>
                                        {barber.disabled ? (
                                            <p className="text-red-400 text-sm mt-1 font-semibold">OFF - Non disponibile</p>
                                        ) : (
                                            <p className="text-green-400 text-sm mt-1">Disponibile per booking</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-zinc-800">
                                <Button
                                    onClick={() => handleToggleAvailability(barber)}
                                    disabled={togglingKey === barber.key}
                                    data-testid={`toggle-barber-${barber.id}`}
                                    className={`w-full font-anton tracking-wider ${
                                        barber.disabled
                                            ? 'bg-green-600 hover:bg-green-700 text-white'
                                            : 'bg-red-600 hover:bg-red-700 text-white'
                                    }`}
                                >
                                    {togglingKey === barber.key ? 'AGGIORNANDO...' : barber.disabled ? 'RIATTIVA BARBIERE' : 'DISATTIVA BARBIERE'}
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// Promotions Management
const PromotionsManagement = () => {
    const [formData, setFormData] = useState({
        title: 'Stay tuned for our new promos',
        message: '',
        cta_text: '',
        cta_link: '',
        start_date: '',
        end_date: ''
    });
    const [vipPerks, setVipPerks] = useState({
        title: 'VIP PERKS',
        description: 'Priority slots and seasonal perks for loyal clients.'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [vipSaving, setVipSaving] = useState(false);

    useEffect(() => {
        const loadBanner = async () => {
            try {
                const [bannerRes, vipRes] = await Promise.all([
                    promotionsAPI.getBanner(),
                    vipPerksAPI.get()
                ]);
                setFormData({
                    title: bannerRes.data.title || 'Stay tuned for our new promos',
                    message: bannerRes.data.message || '',
                    cta_text: bannerRes.data.cta_text || '',
                    cta_link: bannerRes.data.cta_link || '',
                    start_date: bannerRes.data.start_date || '',
                    end_date: bannerRes.data.end_date || ''
                });
                setVipPerks({
                    title: vipRes.data.title || 'VIP PERKS',
                    description: vipRes.data.description || 'Priority slots and seasonal perks for loyal clients.'
                });
            } catch (error) {
                console.error('Failed to load promo banner:', error);
            } finally {
                setLoading(false);
            }
        };
        loadBanner();
    }, []);

    const handleSave = async () => {
        try {
            setSaving(true);
            await promotionsAPI.updateBanner(formData);
            toast.success('Limited offer updated');
        } catch (error) {
            toast.error('Failed to update offer');
        } finally {
            setSaving(false);
        }
    };

    const handleVipSave = async () => {
        try {
            setVipSaving(true);
            await vipPerksAPI.update(vipPerks);
            toast.success('VIP perks updated');
        } catch (error) {
            toast.error('Failed to update VIP perks');
        } finally {
            setVipSaving(false);
        }
    };

    return (
        <div data-testid="admin-offers">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-anton text-3xl">LIMITED OFFERS</h1>
                    <p className="text-zinc-500 font-urbanist text-sm">Edit the promotional banner shown on the website.</p>
                </div>
            </div>

            {loading ? (
                <div className="bg-dark-paper border border-zinc-800 p-6 animate-pulse">
                    <div className="h-6 bg-zinc-800 rounded w-1/2 mb-4" />
                    <div className="h-4 bg-zinc-800 rounded w-3/4" />
                </div>
            ) : (
                <>
                    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
                        <div className="bg-dark-paper border border-zinc-800 p-6 space-y-4">
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Offer title"
                                data-testid="offer-title-input"
                                className="bg-transparent border-zinc-700"
                            />
                            <Textarea
                                value={formData.message}
                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                placeholder="Offer details (optional)"
                                data-testid="offer-message-input"
                                className="bg-transparent border-zinc-700 min-h-[120px]"
                            />
                            <Input
                                value={formData.cta_text}
                                onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                                placeholder="CTA button text (optional)"
                                data-testid="offer-cta-text-input"
                                className="bg-transparent border-zinc-700"
                            />
                            <Input
                                value={formData.cta_link}
                                onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                                placeholder="CTA link (e.g. /book)"
                                data-testid="offer-cta-link-input"
                                className="bg-transparent border-zinc-700"
                            />
                            <div className="grid md:grid-cols-2 gap-4">
                                <input
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                    data-testid="offer-start-date-input"
                                    className="w-full h-9 bg-transparent border border-zinc-700 px-3 text-sm text-white"
                                />
                                <input
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                    data-testid="offer-end-date-input"
                                    className="w-full h-9 bg-transparent border border-zinc-700 px-3 text-sm text-white"
                                />
                            </div>
                            <p className="text-zinc-500 text-xs font-urbanist">
                                Banner auto-expires outside the selected dates. CTA shows only when text + link are provided.
                            </p>
                            <Button
                                onClick={handleSave}
                                data-testid="offer-save-btn"
                                className="bg-primary text-black font-anton tracking-wider"
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'SAVE OFFER'}
                            </Button>
                        </div>

                        <div className="bg-dark-paper border border-zinc-800 p-6" data-testid="offer-preview">
                            <p className="text-zinc-500 text-xs uppercase font-urbanist mb-3">Preview</p>
                            <div className="border border-black bg-accent-yellow text-black px-4 py-4">
                            <span className="font-anton text-lg">{formData.title || 'Stay tuned for our new promos'}</span>
                                {formData.message && (
                                    <p className="font-urbanist text-sm mt-2">{formData.message}</p>
                                )}
                                {formData.cta_text && (
                                    <div className="mt-3 inline-flex items-center px-4 py-2 bg-black text-white font-anton text-sm">
                                        {formData.cta_text}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 grid lg:grid-cols-[1.2fr_1fr] gap-6">
                        <div className="bg-dark-paper border border-zinc-800 p-6 space-y-4">
                            <h2 className="font-anton text-2xl">VIP PERKS</h2>
                            <Input
                                value={vipPerks.title}
                                onChange={(e) => setVipPerks({ ...vipPerks, title: e.target.value })}
                                placeholder="VIP perks title"
                                data-testid="vip-title-input"
                                className="bg-transparent border-zinc-700"
                            />
                            <Textarea
                                value={vipPerks.description}
                                onChange={(e) => setVipPerks({ ...vipPerks, description: e.target.value })}
                                placeholder="VIP perks description"
                                data-testid="vip-description-input"
                                className="bg-transparent border-zinc-700 min-h-[120px]"
                            />
                            <Button
                                onClick={handleVipSave}
                                data-testid="vip-save-btn"
                                className="bg-primary text-black font-anton tracking-wider"
                                disabled={vipSaving}
                            >
                                {vipSaving ? 'Saving...' : 'SAVE VIP PERKS'}
                            </Button>
                        </div>

                        <div className="bg-dark-paper border border-zinc-800 p-6" data-testid="vip-preview">
                            <p className="text-zinc-500 text-xs uppercase font-urbanist mb-3">Preview</p>
                            <div className="border border-zinc-800 bg-dark px-4 py-4">
                                <span className="font-anton text-lg">{vipPerks.title || 'VIP PERKS'}</span>
                                <p className="font-urbanist text-sm mt-2 text-zinc-400">
                                    {vipPerks.description || 'Priority slots and seasonal perks for loyal clients.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// Media Management
const MediaManagement = () => {
    const [mediaSettings, setMediaSettings] = useState({
        hero_image: '',
        product_image: '',
        instagram_images: ['', '', '', ''],
        service_images: {},
        featured_services: []
    });
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadingKey, setUploadingKey] = useState('');

    useEffect(() => {
        const loadMedia = async () => {
            try {
                const [mediaRes, servicesRes] = await Promise.all([
                    mediaAPI.getSettings(),
                    servicesAPI.getAll()
                ]);
                const mediaData = mediaRes?.data || {};
                setMediaSettings({
                    hero_image: mediaData.hero_image || '',
                    product_image: mediaData.product_image || '',
                    instagram_images: mediaData.instagram_images?.length ? mediaData.instagram_images : ['', '', '', ''],
                    service_images: mediaData.service_images || {},
                    featured_services: mediaData.featured_services || []
                });
                setServices(servicesRes.data);
            } catch (error) {
                console.error('Failed to load media settings:', error);
            } finally {
                setLoading(false);
            }
        };
        loadMedia();
    }, []);

    const resolveMediaUrl = (value) => {
        if (!value) return '';
        if (value.startsWith('http')) return value;
        return `${process.env.REACT_APP_BACKEND_URL}${value}`;
    };

    const saveSettings = async (nextSettings, successMessage) => {
        try {
            setMediaSettings(nextSettings);
            await mediaAPI.updateSettings(nextSettings);
            if (successMessage) {
                toast.success(successMessage);
            }
        } catch (error) {
            toast.error('Failed to update images');
        }
    };

    const handleUpload = async (file, updater, key) => {
        if (!file) return;
        try {
            setUploadingKey(key);
            const formData = new FormData();
            formData.append('file', file);
            const response = await mediaAPI.upload(formData);
            const nextSettings = updater(response.data.url);
            await saveSettings(nextSettings, 'Image updated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Upload failed');
        } finally {
            setUploadingKey('');
        }
    };

    const handleClear = async (updater) => {
        const nextSettings = updater('');
        await saveSettings(nextSettings, 'Image removed');
    };

    return (
        <div data-testid="admin-media">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-anton text-3xl">MEDIA</h1>
                    <p className="text-zinc-500 font-urbanist text-sm">Upload and manage website images.</p>
                </div>
            </div>

            {loading ? (
                <div className="bg-dark-paper border border-zinc-800 p-6 animate-pulse">
                    <div className="h-6 bg-zinc-800 rounded w-1/2 mb-4" />
                    <div className="h-4 bg-zinc-800 rounded w-3/4" />
                </div>
            ) : (
                <div className="space-y-8">
                    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
                        <div className="bg-dark-paper border border-zinc-800 p-6 space-y-4">
                            <h2 className="font-anton text-2xl">Hero Background</h2>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                {mediaSettings.hero_image ? (
                                    <img
                                        src={resolveMediaUrl(mediaSettings.hero_image)}
                                        alt="Hero"
                                        className="h-28 w-44 object-cover border border-zinc-800"
                                    />
                                ) : (
                                    <div className="h-28 w-44 border border-dashed border-zinc-700 flex items-center justify-center text-zinc-600 text-sm">
                                        No image
                                    </div>
                                )}
                                <div className="flex gap-3">
                                    <label
                                        htmlFor="hero-image-input"
                                        className="px-4 py-2 border border-zinc-700 text-zinc-300 font-urbanist cursor-pointer"
                                        data-testid="hero-image-upload-btn"
                                    >
                                        {uploadingKey === 'hero' ? 'Uploading...' : 'Upload'}
                                    </label>
                                    <input
                                        id="hero-image-input"
                                        type="file"
                                        accept="image/*"
                                        data-testid="hero-image-input"
                                        className="hidden"
                                        onChange={(e) => handleUpload(e.target.files?.[0], (url) => ({
                                            ...mediaSettings,
                                            hero_image: url
                                        }), 'hero')}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleClear((url) => ({ ...mediaSettings, hero_image: url }))}
                                        data-testid="hero-image-clear"
                                        className="px-4 py-2 border border-zinc-800 text-zinc-500 hover:text-white"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="bg-dark-paper border border-zinc-800 p-6 space-y-4">
                            <h2 className="font-anton text-2xl">El Patron Product Image</h2>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                {mediaSettings.product_image ? (
                                    <img
                                        src={resolveMediaUrl(mediaSettings.product_image)}
                                        alt="El Patron"
                                        className="h-28 w-44 object-cover border border-zinc-800"
                                    />
                                ) : (
                                    <div className="h-28 w-44 border border-dashed border-zinc-700 flex items-center justify-center text-zinc-600 text-sm">
                                        No image
                                    </div>
                                )}
                                <div className="flex gap-3">
                                    <label
                                        htmlFor="product-image-input"
                                        className="px-4 py-2 border border-zinc-700 text-zinc-300 font-urbanist cursor-pointer"
                                        data-testid="product-image-upload-btn"
                                    >
                                        {uploadingKey === 'product' ? 'Uploading...' : 'Upload'}
                                    </label>
                                    <input
                                        id="product-image-input"
                                        type="file"
                                        accept="image/*"
                                        data-testid="product-image-input"
                                        className="hidden"
                                        onChange={(e) => handleUpload(e.target.files?.[0], (url) => ({
                                            ...mediaSettings,
                                            product_image: url
                                        }), 'product')}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleClear((url) => ({ ...mediaSettings, product_image: url }))}
                                        data-testid="product-image-clear"
                                        className="px-4 py-2 border border-zinc-800 text-zinc-500 hover:text-white"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-dark-paper border border-zinc-800 p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="font-anton text-2xl">Instagram Grid</h2>
                            <span className="text-zinc-500 text-sm font-urbanist">4 slots</span>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {mediaSettings.instagram_images.map((img, index) => (
                                <div key={`insta-slot-${index}`} className="border border-zinc-800 p-4 space-y-3">
                                    <div className="h-32 bg-zinc-900 flex items-center justify-center">
                                        {img ? (
                                            <img
                                                src={resolveMediaUrl(img)}
                                                alt={`Instagram ${index + 1}`}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <span className="text-zinc-600 text-sm">No image</span>
                                        )}
                                    </div>
                                    <label
                                        htmlFor={`instagram-image-input-${index}`}
                                        className="block w-full text-center px-3 py-2 border border-zinc-700 text-zinc-300 font-urbanist cursor-pointer"
                                        data-testid={`instagram-upload-btn-${index}`}
                                    >
                                        {uploadingKey === `instagram-${index}` ? 'Uploading...' : 'Upload'}
                                    </label>
                                    <input
                                        id={`instagram-image-input-${index}`}
                                        type="file"
                                        accept="image/*"
                                        data-testid={`instagram-image-input-${index}`}
                                        className="hidden"
                                        onChange={(e) => handleUpload(e.target.files?.[0], (url) => {
                                            const nextImages = [...mediaSettings.instagram_images];
                                            nextImages[index] = url;
                                            return { ...mediaSettings, instagram_images: nextImages };
                                        }, `instagram-${index}`)}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleClear((url) => {
                                            const nextImages = [...mediaSettings.instagram_images];
                                            nextImages[index] = url;
                                            return { ...mediaSettings, instagram_images: nextImages };
                                        })}
                                        data-testid={`instagram-clear-${index}`}
                                        className="w-full px-3 py-2 border border-zinc-800 text-zinc-500 hover:text-white"
                                    >
                                        Clear
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-dark-paper border border-zinc-800 p-6" data-testid="service-images-section">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="font-anton text-2xl">Service Images</h2>
                            <span className="text-zinc-500 text-sm font-urbanist">{services.length} services</span>
                        </div>
                        <p className="text-zinc-500 text-sm font-urbanist mb-4">
                            Click the <span className="text-yellow-400">&#9733;</span> star to feature a service on the landing page (max 3).
                            Featured: {(mediaSettings.featured_services || []).length}/3
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                            {services.map((service) => {
                                const isFeatured = (mediaSettings.featured_services || []).includes(service.key);
                                return (
                                <div key={service.key} className={`border p-4 flex items-center justify-between gap-4 ${isFeatured ? 'border-primary bg-primary/5' : 'border-zinc-800'}`}>
                                    <div className="flex items-center gap-4">
                                        <button
                                            type="button"
                                            data-testid={`service-feature-toggle-${service.key}`}
                                            className={`text-2xl transition-colors ${isFeatured ? 'text-yellow-400' : 'text-zinc-700 hover:text-yellow-400/50'}`}
                                            title={isFeatured ? 'Remove from featured' : 'Add to featured on homepage'}
                                            onClick={async () => {
                                                const current = mediaSettings.featured_services || [];
                                                let next;
                                                if (isFeatured) {
                                                    next = current.filter(k => k !== service.key);
                                                } else {
                                                    if (current.length >= 3) {
                                                        toast.error('Max 3 featured services. Remove one first.');
                                                        return;
                                                    }
                                                    next = [...current, service.key];
                                                }
                                                await saveSettings({ ...mediaSettings, featured_services: next }, isFeatured ? 'Removed from featured' : 'Added to featured');
                                            }}
                                        >
                                            {isFeatured ? '\u2605' : '\u2606'}
                                        </button>
                                        {mediaSettings.service_images?.[service.key] ? (
                                            <img
                                                src={resolveMediaUrl(mediaSettings.service_images[service.key])}
                                                alt={service.name}
                                                className="h-16 w-16 object-cover border border-zinc-800"
                                            />
                                        ) : (
                                            <div className="h-16 w-16 border border-dashed border-zinc-700 flex items-center justify-center text-zinc-600 text-xs">
                                                No image
                                            </div>
                                        )}
                                        <div>
                                            <p className="font-urbanist text-white font-semibold">{service.name}</p>
                                            <p className="text-zinc-500 text-xs">€{service.price} · {service.duration} min</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <label
                                            htmlFor={`service-image-input-${service.key}`}
                                            className="px-3 py-2 border border-zinc-700 text-zinc-300 font-urbanist cursor-pointer text-sm text-center"
                                            data-testid={`service-upload-btn-${service.key}`}
                                        >
                                            {uploadingKey === `service-${service.key}` ? 'Uploading...' : 'Upload'}
                                        </label>
                                        <input
                                            id={`service-image-input-${service.key}`}
                                            type="file"
                                            accept="image/*"
                                            data-testid={`service-image-input-${service.key}`}
                                            className="hidden"
                                            onChange={(e) => handleUpload(e.target.files?.[0], (url) => ({
                                                ...mediaSettings,
                                                service_images: {
                                                    ...mediaSettings.service_images,
                                                    [service.key]: url
                                                }
                                            }), `service-${service.key}`)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleClear((url) => ({
                                                ...mediaSettings,
                                                service_images: {
                                                    ...mediaSettings.service_images,
                                                    [service.key]: url
                                                }
                                            }))}
                                            data-testid={`service-clear-${service.key}`}
                                            className="px-3 py-2 border border-zinc-800 text-zinc-500 hover:text-white text-sm"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Main Admin Dashboard
const AdminDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);

    return (
        <div className="min-h-screen bg-dark">
            <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

            {/* Main Content */}
            <div className="lg:ml-64">
                {/* Top Bar */}
                <header className="sticky top-0 z-30 bg-dark border-b border-zinc-800 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden text-white p-2"
                            data-testid="mobile-sidebar-btn"
                        >
                            <Menu size={24} />
                        </button>
                        <div className="flex-1" />
                        <Link to="/" className="text-zinc-500 hover:text-white font-urbanist text-sm" data-testid="admin-view-site-link">
                            View Site →
                        </Link>
                    </div>
                </header>

                {/* Page Content */}
                <main className="p-6 md:p-8">
                    <Routes>
                        <Route index element={<DashboardOverview />} />
                        <Route path="appointments" element={<AppointmentsManagement />} />
                        <Route path="services" element={<ServicesManagement />} />
                        <Route path="barbers" element={<BarbersManagement />} />
                        <Route path="offers" element={<PromotionsManagement />} />
                        <Route path="media" element={<MediaManagement />} />
                    </Routes>
                </main>
            </div>
        </div>
    );
};

export default AdminDashboard;
